<template>
    <div class="dashboard-surveycard">
        <DxChart :data-source="chartDataSource" :title="{ text: props.data.title, font: {size: 14} }" :palette="selectPalette(props.data)">
            <DxSize :height="400" />
            <DxSeries type="bar" name="Monthly data" argument-field="date" value-field="count" />
            <DxSeries type="spline" name="Total data" argument-field="date" value-field="total">
                <DxPoint :visible="true" :size="8" symbol="polygon" />
            </DxSeries>
            <DxCommonAxisSettings :allow-decimals="false" />
            <DxTooltip :enabled="true" />
            <DxExport :enabled="true" />
            <DxLegend :visible="false" />
        </DxChart>
        <div class="card-details-separator undo-paddings" />
        <div class="card-details undo-paddings">
            <div class="label">Count:</div>
            <div>{{ data.count.toLocaleString() }}</div>
        </div>
        <div class="card-details undo-paddings">
            <div class="label">Last:</div>
            <div v-if="data.last_updated"><span class="date-ago">{{ Math.floor((new Date() - new Date(data.last_updated)) / (1000 * 60 * 60 * 24)) }}d ago,</span> {{ data.last_updated.substr(0, 10) }}</div>
            <div v-else>Never</div>
        </div>
        <div class="card-details undo-paddings">
            <div class="label">Started:</div>
            <div v-if="data.first_created"><span class="date-ago">{{ Math.floor((new Date() - new Date(data.first_created)) / (1000 * 60 * 60 * 24)) }}d ago,</span> {{ data.first_created.substr(0, 10) }}</div>
            <div v-else>Not started yet</div>
        </div>
        <div class="card-details undo-paddings" v-if="data.description">
            <div>{{ data.description }}</div>
        </div>
        <div class="card-details-end undo-paddings" />
        <div class="card-actions">
            <DxButton text="View Data" icon="inserttable" :disabled="data.count <= 0" @click="$emit('view-data', data.id)"/>
            <DxButton text="Expand" icon="codeblock" :disabled="data.count <= 0" @click="$emit('expand-card', data.id)" />
        </div>
    </div>
</template>

<script setup>
import { defineProps, watch } from 'vue';
import { DxChart, DxSeries, DxLegend, DxTooltip, DxExport, DxPoint, DxSize, DxCommonAxisSettings } from 'devextreme-vue/chart';
import { DxButton } from 'devextreme-vue/button';
import DataSource from 'devextreme/data/data_source';

const props = defineProps({
    data: Object,               // NB: Subdata in this MUST be sorted by date already!
    showMonths: Number,
});

const graphData = [];
const chartDataSource = new DataSource({
    store: graphData,
});

const generateChartData = () => {
    // We want to only show the last <showMonths> months of data in our charts. The data is already sorted, but may be non-contiguous.
    // But we still need to iterate over all data -- because we want to know running total counts for each individual month.
    const today = new Date();
    const pastMonthsDate = new Date(today.getFullYear(), today.getMonth() - props.showMonths, 1);
    graphData.length = 0;
    let runningTotal = 0;
    props.data.subdata.forEach(item => {
        // Each subdata item has a month property as a string in format yyyy-mm.
        const itemCount = item.count || 0;
        runningTotal += itemCount;
        const itemDate = new Date(item.month + '-01');
        if (itemDate >= pastMonthsDate) {
            graphData.push({
                date: item.month,
                count: itemCount,
                total: runningTotal,
            });
        }
    });
    // Insert 'empty' values for months that don't have any data. This is non-trivial since we need to preserve our running totals for months inserted.    
    for (let i = 0; i < props.showMonths; i++) {
        const month = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const monthStr = month.getFullYear() + '-' + String(month.getMonth() + 1).padStart(2, '0');
        if (!graphData.some(item => item.date === monthStr)) {
            graphData.unshift({ date: monthStr, count: 0, total: 0 });
        }
    }
    // Resort since empty data can be non-contiguously added (e.g. we have data for nov, but not dec and now it's jan)
    graphData.sort((a, b) => new Date(a.date) - new Date(b.date));
    // Then fill in 'running total' values for months that don't have any data.
    // Select the starting date - the oldest date from either subdata[0] or the graphData window.
    const oldestDate = props.data.subdata.length > 0
        ? new Date(Math.min(new Date(props.data.subdata[0].month + '-01'), new Date(graphData[0].date + '-01')))
        : new Date(graphData[0].date + '-01');
    const newestDate = new Date(today.getFullYear(), today.getMonth(), 1);
    runningTotal = 0;
    // Sanity check: iterate forward a maximum for 2000 times. Data shouldn't be more than ~150 years apart!
    for (var i = 0; i < 2000; i++) {
        const currentDate = new Date(oldestDate.getFullYear(), oldestDate.getMonth() + i, 1);
        const currentDateStr = currentDate.getFullYear() + '-' + String(currentDate.getMonth() + 1).padStart(2, '0');
        const valueForGraph = graphData.find(item => item.date == currentDateStr);
        const valueForSubdata = props.data.subdata.find(item => item.month == currentDateStr);
        if (i === 0) {      // Start off our running total with the first date
            runningTotal = valueForSubdata && valueForSubdata.count ? valueForSubdata.count : 0;
        } else {
            runningTotal += valueForSubdata && valueForSubdata.count ? valueForSubdata.count : (valueForGraph && valueForGraph.count ? valueForGraph.count : 0);
        }
        if (valueForGraph) {
            valueForGraph.total = runningTotal;
        }
        if (currentDate.getFullYear() > newestDate.getFullYear() || (currentDate.getFullYear() === newestDate.getFullYear() && currentDate.getMonth() >= newestDate.getMonth())) {
            break;
        }
    }

    chartDataSource.reload();
};
generateChartData();

// If props changes, update the chart data source to allow for more 'empty' months.
watch(props, () => {
    generateChartData();
});

// Select a palette based on the survey type.
const selectPalette = (data) => {
    return data.id.startsWith('survey:_bhub_') ? 'Harmony Light' : 'Material';
};

</script>

<style scoped lang="scss">
.card-details-separator {
    margin-top: 10px;
    height: 10px;
    background-color: #eee;
}
.card-details {
    display: flex;
    gap: 2px;
    align-items: flex-start;
    justify-content: space-between;
    padding: 5px 10px;
    background-color: #eee;
    
    .screen-large & {
        padding: 2px 20px;
    }
    .label {
        background-color: rgba(3, 169, 244, 0.2);
        border-radius: 4px;
        padding: 2px 5px;
        width: 60px;
        text-align: right;
    }
}
.card-details-end {
    height: 10px;
    background-color: #eee;
}
.card-actions {
    width: 100%;
    margin: 1em 0;
    display: flex;
    justify-content: space-between;
}
.dashboard-surveycard {
    margin-bottom: -10px;

    .screen-large & {
        margin-bottom: -20px;
    }
}
</style>
